import { Box, styled } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Flex from 'ui/Flex/Flex';

import SeeMoreButton from './SeeMoreButton';

const StyChipsListContainer = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== 'showMoreChips' && prop !== 'defaultMaxHeight',
})<{ showMoreChips: boolean; defaultMaxHeight: string }>(
    ({ showMoreChips, defaultMaxHeight, theme }) => ({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'flex-start',
        gap: theme.spacing(1),
        ...(showMoreChips ? {} : { maxHeight: defaultMaxHeight }),
        overflowY: showMoreChips ? 'auto' : 'hidden',
        width: '100%',
    }),
);

type AdaptiveChipListProps = {
    children: React.ReactNode;
    defaultMaxHeight: string;
    chipsListLength: number;
};

const AdaptiveChipList = ({
    defaultMaxHeight,
    children,
    chipsListLength,
}: AdaptiveChipListProps): JSX.Element => {
    const chipsContainerRef = useRef<HTMLDivElement>(null);
    const [showMoreChips, setShowMoreChips] = useState(false);
    const [areChipsOverflowing, setAreChipsOverflowing] = useState(false);
    useEffect(() => {
        const chipsContainer = chipsContainerRef.current;
        if (chipsContainer) {
            setAreChipsOverflowing(
                chipsContainer.scrollHeight >
                    Math.min(
                        chipsContainer.clientHeight,
                        parseInt(defaultMaxHeight.replace('px', ''), 10),
                    ),
            );
        }
    }, [chipsListLength, defaultMaxHeight]);

    return (
        <Flex direction="column" alignItems="center" width="100%">
            <StyChipsListContainer
                showMoreChips={showMoreChips}
                defaultMaxHeight={defaultMaxHeight}
                ref={chipsContainerRef}
            >
                {children}
            </StyChipsListContainer>
            {areChipsOverflowing && (
                <SeeMoreButton
                    showMore={showMoreChips}
                    onClick={() => setShowMoreChips(!showMoreChips)}
                />
            )}
        </Flex>
    );
};

export default AdaptiveChipList;
